import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Target, Plus, TrendingUp, Award, Users, Star, ChevronDown, ChevronUp, CalendarDays, IndianRupee, ClipboardList, Save, FileText, CheckSquare, Bell, Upload, Download, MessageSquare, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { getUser, isAdmin } from '@/lib/auth';

const BAND_COLORS = {
  Excellent: 'bg-emerald-100 text-emerald-800',
  Good: 'bg-blue-100 text-blue-800',
  Average: 'bg-amber-100 text-amber-800',
  'Below Threshold': 'bg-red-100 text-red-800',
};
const STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-600',
  submitted: 'bg-blue-100 text-blue-700',
  finalized: 'bg-emerald-100 text-emerald-700',
};

function ScoreInput({ value, max, onChange }) {
  return (
    <Input
      type="number"
      min="0"
      max={max}
      step="0.5"
      value={value ?? ''}
      onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      className="w-20 h-8 text-sm text-center"
      placeholder="—"
    />
  );
}

function KraScoreRow({ item, isEditing, onScoreChange, onRemarksChange }) {
  const pct = item.marks_allotted > 0 && item.marks_scored != null
    ? Math.round((item.marks_scored / item.marks_allotted) * 100)
    : null;

  return (
    <TableRow className="hover:bg-slate-50/50" data-testid={`kra-row-${item.kra_item_id}`}>
      <TableCell className="text-sm max-w-xs">
        <div className="font-medium leading-tight">{item.description}</div>
        {item.frequency && <span className="text-xs text-slate-400 mt-0.5 block">{item.frequency}</span>}
      </TableCell>
      <TableCell className="text-center text-sm font-semibold text-slate-600">{item.marks_allotted}</TableCell>
      <TableCell className="text-center">
        {isEditing ? (
          <ScoreInput value={item.marks_scored} max={item.marks_allotted} onChange={onScoreChange} />
        ) : (
          <span className={`text-sm font-semibold ${item.marks_scored != null ? 'text-[#0D2850]' : 'text-slate-300'}`}>
            {item.marks_scored ?? '—'}
          </span>
        )}
      </TableCell>
      <TableCell className="text-center">
        {pct != null ? (
          <div className="flex items-center gap-2 justify-center">
            <Progress value={pct} className="w-16 h-2" />
            <span className="text-xs text-slate-500">{pct}%</span>
          </div>
        ) : <span className="text-xs text-slate-300">—</span>}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <Input
            value={item.remarks || ''}
            onChange={e => onRemarksChange(e.target.value)}
            className="h-8 text-xs"
            placeholder="Remarks..."
          />
        ) : (
          <span className="text-xs text-slate-500">{item.remarks || ''}</span>
        )}
      </TableCell>
    </TableRow>
  );
}

export default function PerformancePage() {
  const user = getUser();
  const admin = isAdmin() || user?.role === 'hr';
  const MANAGER_ROLES = ['manager', 'director', 'director_hr', 'director_operations', 'director_sales'];
  const canManageTeam = admin || MANAGER_ROLES.includes(user?.role);
  const [tab, setTab] = useState(canManageTeam ? 'monthly-all' : 'my-evaluation');
  const [periods, setPeriods] = useState({ months: [], quarters: [], current_month: '', current_quarter: '' });
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [quarterly, setQuarterly] = useState(null);
  const [allMonthly, setAllMonthly] = useState([]);
  const [allQuarterly, setAllQuarterly] = useState([]);
  const [bands, setBands] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editScores, setEditScores] = useState([]);
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [empEval, setEmpEval] = useState(null);
  const [empEditing, setEmpEditing] = useState(false);
  const [empEditScores, setEmpEditScores] = useState([]);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemEmpId, setAddItemEmpId] = useState('');
  const [addItemForm, setAddItemForm] = useState({ description: '', frequency: '', marks_allotted: '5', category: 'task' });
  const [kraDefn, setKraDefn] = useState(null);
  const [viewDefnOpen, setViewDefnOpen] = useState(false);
  const [viewDefnEmpId, setViewDefnEmpId] = useState('');
  const [myCheckin, setMyCheckin] = useState({ checked_kras: [] });
  const [myKraDefs, setMyKraDefs] = useState(null);
  const [teamCheckins, setTeamCheckins] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [empWithoutKra, setEmpWithoutKra] = useState([]);
  const [selectedNewMember, setSelectedNewMember] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('replace');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [empRemarksEditing, setEmpRemarksEditing] = useState(false);
  const [empRemarksScores, setEmpRemarksScores] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, bRes] = await Promise.all([
          api.get('/api/performance/periods'),
          api.get('/api/performance/score-bands'),
        ]);
        setPeriods(pRes.data);
        setBands(bRes.data);
        if (!selectedMonth && pRes.data.current_month) setSelectedMonth(pRes.data.current_month);
        if (!selectedQuarter && pRes.data.current_quarter) setSelectedQuarter(pRes.data.current_quarter);
      } catch (e) { console.error(e); }
    })();
  }, []); // eslint-disable-line

  const loadMyEval = useCallback(async () => {
    if (!selectedMonth) return;
    try {
      const res = await api.get('/api/performance/evaluations', { params: { month: selectedMonth } });
      setEvaluation(res.data);
      setIsEditing(false);
    } catch (e) { console.error(e); }
  }, [selectedMonth]);

  const loadMyQuarterly = useCallback(async () => {
    if (!selectedQuarter) return;
    try {
      const res = await api.get('/api/performance/quarterly', { params: { quarter: selectedQuarter } });
      setQuarterly(res.data);
    } catch (e) { console.error(e); }
  }, [selectedQuarter]);

  const loadAllMonthly = useCallback(async () => {
    if (!selectedMonth || !canManageTeam) return;
    try {
      const res = await api.get('/api/performance/all', { params: { month: selectedMonth } });
      setAllMonthly(res.data);
    } catch (e) { console.error(e); }
  }, [selectedMonth, canManageTeam]);

  const loadAllQuarterly = useCallback(async () => {
    if (!selectedQuarter || !canManageTeam) return;
    try {
      const res = await api.get('/api/performance/all', { params: { quarter: selectedQuarter } });
      setAllQuarterly(res.data);
    } catch (e) { console.error(e); }
  }, [selectedQuarter, canManageTeam]);

  useEffect(() => { loadMyEval(); }, [loadMyEval]);
  useEffect(() => { loadMyQuarterly(); }, [loadMyQuarterly]);
  useEffect(() => { loadAllMonthly(); }, [loadAllMonthly]);
  useEffect(() => { loadAllQuarterly(); }, [loadAllQuarterly]);

  const startEditing = (scores) => {
    setEditScores(scores.map(s => ({ ...s })));
    setIsEditing(true);
  };

  const updateScore = (idx, field, value) => {
    setEditScores(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const saveMyScores = async () => {
    try {
      await api.post('/api/performance/evaluations/submit', {
        employee_id: evaluation.employee_id,
        month: selectedMonth,
        kra_scores: editScores.map(s => ({
          kra_item_id: s.kra_item_id,
          description: s.description,
          marks_allotted: s.marks_allotted,
          marks_scored: s.marks_scored,
          remarks: s.remarks || '',
        })),
      });
      toast.success('Scores saved');
      setIsEditing(false);
      loadMyEval();
    } catch (e) { toast.error('Failed to save'); }
  };

  const loadEmpEval = async (empId) => {
    if (expandedEmp === empId && empEval) { setExpandedEmp(null); setEmpEval(null); setEmpEditing(false); return; }
    try {
      const res = await api.get('/api/performance/evaluations', { params: { employee_id: empId, month: selectedMonth } });
      setEmpEval(res.data);
      setExpandedEmp(empId);
      setEmpEditing(false);
    } catch (e) { toast.error('Failed to load'); }
  };

  const startEmpEditing = () => {
    setEmpEditScores(empEval.kra_scores.map(s => ({ ...s })));
    setEmpEditing(true);
  };

  const updateEmpScore = (idx, field, value) => {
    setEmpEditScores(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const saveEmpScores = async () => {
    try {
      await api.post('/api/performance/evaluations/submit', {
        employee_id: expandedEmp,
        month: selectedMonth,
        kra_scores: empEditScores.map(s => ({
          kra_item_id: s.kra_item_id,
          description: s.description,
          marks_allotted: s.marks_allotted,
          marks_scored: s.marks_scored,
          remarks: s.remarks || '',
        })),
      });
      toast.success('Scores saved');
      setEmpEditing(false);
      loadEmpEval(expandedEmp);
      loadAllMonthly();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleAddItem = async () => {
    if (!addItemForm.description) { toast.error('Description required'); return; }
    try {
      await api.post(`/api/performance/kra-definitions/${addItemEmpId}/item`, {
        description: addItemForm.description,
        frequency: addItemForm.frequency,
        marks_allotted: parseFloat(addItemForm.marks_allotted) || 2.5,
        category: addItemForm.category,
      });
      toast.success('KRA item added');
      setAddItemOpen(false);
      setAddItemForm({ description: '', frequency: '', marks_allotted: '5', category: 'task' });
      loadAllMonthly();
      if (expandedEmp === addItemEmpId) loadEmpEval(addItemEmpId);
    } catch (e) { toast.error('Failed to add'); }
  };

  const loadKraDefn = async (empId) => {
    try {
      const res = await api.get('/api/performance/kra-definitions', { params: { employee_id: empId } });
      setKraDefn(res.data);
      setViewDefnEmpId(empId);
      setViewDefnOpen(true);
    } catch (e) { toast.error('Failed to load'); }
  };

  const deleteKraItem = async (itemId) => {
    if (!viewDefnEmpId) return;
    if (!window.confirm('Remove this KRA? Any scored data for this KRA will be preserved but detached.')) return;
    try {
      await api.delete(`/api/performance/kra-definitions/${viewDefnEmpId}/item/${itemId}`);
      const res = await api.get('/api/performance/kra-definitions', { params: { employee_id: viewDefnEmpId } });
      setKraDefn(res.data);
      toast.success('KRA removed');
      loadAllMonthly();
      if (expandedEmp === viewDefnEmpId) loadEmpEval(viewDefnEmpId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const monthLabel = (m) => periods.months.find(p => p.name === m)?.label || m;

  // Daily Check-in functions
  const loadMyCheckin = useCallback(async () => {
    try {
      const [checkinRes, defsRes] = await Promise.all([
        api.get('/api/performance/daily-checkin'),
        api.get('/api/performance/kra-definitions'),
      ]);
      setMyCheckin(checkinRes.data);
      setMyKraDefs(defsRes.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadTeamCheckins = useCallback(async () => {
    if (!canManageTeam) return;
    try {
      const res = await api.get('/api/performance/daily-checkin/team');
      setTeamCheckins(res.data);
    } catch (e) { console.error(e); }
  }, [canManageTeam]);

  useEffect(() => {
    if (tab === 'daily-checkin') { loadMyCheckin(); loadTeamCheckins(); }
  }, [tab, loadMyCheckin, loadTeamCheckins]);

  const toggleCheckin = async (kraId) => {
    const current = myCheckin?.checked_kras || [];
    const currentIds = current.map(c => (typeof c === 'string' ? c : c.kra_id));
    let updated;
    if (currentIds.includes(kraId)) {
      updated = current.filter(c => (typeof c === 'string' ? c : c.kra_id) !== kraId);
    } else {
      updated = [...current, { kra_id: kraId, remarks: '' }];
    }
    try {
      await api.post('/api/performance/daily-checkin', { checked_kras: updated });
      setMyCheckin(prev => ({ ...prev, checked_kras: updated }));
    } catch (e) { toast.error('Failed'); }
  };

  const updateCheckinRemarks = async (kraId, remarks) => {
    const current = myCheckin?.checked_kras || [];
    const updated = current.map(c => {
      const cid = typeof c === 'string' ? c : c.kra_id;
      return cid === kraId ? { kra_id: cid, remarks } : (typeof c === 'string' ? { kra_id: c, remarks: '' } : c);
    });
    setMyCheckin(prev => ({ ...prev, checked_kras: updated }));
  };

  const persistCheckinRemarks = async () => {
    const current = myCheckin?.checked_kras || [];
    try {
      await api.post('/api/performance/daily-checkin', { checked_kras: current });
    } catch (e) { toast.error('Failed to save remarks'); }
  };

  const isKraChecked = (kraId) => {
    const current = myCheckin?.checked_kras || [];
    return current.some(c => (typeof c === 'string' ? c : c.kra_id) === kraId);
  };

  const getCheckinRemarks = (kraId) => {
    const current = myCheckin?.checked_kras || [];
    const entry = current.find(c => (typeof c === 'string' ? c : c.kra_id) === kraId);
    return typeof entry === 'string' ? '' : (entry?.remarks || '');
  };

  const startEmpRemarksEdit = () => {
    setEmpRemarksScores((evaluation?.kra_scores || []).map(s => ({
      kra_item_id: s.kra_item_id,
      description: s.description,
      category: s.category,
      employee_remarks: s.employee_remarks || '',
    })));
    setEmpRemarksEditing(true);
  };

  const updateEmpRemarks = (idx, value) => {
    setEmpRemarksScores(prev => prev.map((s, i) => i === idx ? { ...s, employee_remarks: value } : s));
  };

  const saveEmpRemarks = async () => {
    try {
      await api.post('/api/performance/evaluations/employee-remarks', {
        month: selectedMonth,
        kra_remarks: empRemarksScores.map(s => ({ kra_item_id: s.kra_item_id, employee_remarks: s.employee_remarks })),
      });
      toast.success('Monthly remarks saved');
      setEmpRemarksEditing(false);
      loadMyEval();
    } catch (e) { toast.error('Failed to save'); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/api/performance/kra-definitions/excel-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kra-upload-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      const status = e.response?.status;
      toast.error(status ? `Download failed (HTTP ${status}) — please re-login and retry` : 'Download failed — check your internet connection');
    }
  };

  const submitExcelUpload = async () => {
    if (!uploadFile) { toast.error('Select a file first'); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append('file', uploadFile);
      const res = await api.post(`/api/performance/kra-definitions/upload-excel?mode=${uploadMode}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      toast.success(res.data.message);
      loadAllMonthly();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Upload failed');
      toast.error(msg);
      if (detail && typeof detail === 'object') setUploadResult(detail);
    }
    setUploading(false);
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await api.post('/api/performance/send-reminders');
      toast.success(res.data.message);
    } catch (e) { toast.error('Failed'); }
    setSendingReminders(false);
  };

  const loadEmpWithoutKra = async () => {
    try {
      const res = await api.get('/api/performance/employees-without-kra');
      setEmpWithoutKra(res.data);
      setSelectedNewMember('');
      setAddMemberOpen(true);
    } catch (e) { toast.error('Failed to load'); }
  };

  const handleAddMember = async () => {
    if (!selectedNewMember) { toast.error('Select an employee'); return; }
    setAddingMember(true);
    try {
      const res = await api.post('/api/performance/kra-definitions/add-member', { employee_id: selectedNewMember });
      toast.success(res.data.message);
      setAddMemberOpen(false);
      loadAllMonthly();
      // Auto-open add KRA item dialog for the new member
      if (res.data.needs_task_kras) {
        setAddItemEmpId(selectedNewMember);
        setAddItemOpen(true);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setAddingMember(false);
  };

  const renderKraTable = (scores, editing, editData, onScoreChange, onRemarksChange) => {
    const taskKras = (editing ? editData : scores).filter(s => s.category === 'Task-Based');
    const nonTaskKras = (editing ? editData : scores).filter(s => s.category === 'Non-Task-Based');
    const allItems = editing ? editData : scores;
    const totalAllotted = allItems.reduce((s, k) => s + (k.marks_allotted || 0), 0);
    const totalScored = allItems.reduce((s, k) => s + (k.marks_scored || 0), 0);
    const scoredCount = allItems.filter(k => k.marks_scored != null).length;
    const pct = totalAllotted > 0 && scoredCount > 0 ? Math.round((totalScored / totalAllotted) * 100) : 0;

    return (
      <>
        <div className="flex items-center justify-between p-3 bg-[#0D2850]/5 rounded-lg mb-3">
          <div className="flex items-center gap-6">
            <div>
              <span className="text-xs text-slate-500 uppercase tracking-wider">Total Score</span>
              <div className="text-xl font-bold text-[#0D2850]">{totalScored}<span className="text-sm font-normal text-slate-400"> / {totalAllotted}</span></div>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase tracking-wider">Percentage</span>
              <div className="text-xl font-bold text-[#0D2850]">{pct}%</div>
            </div>
            <Progress value={pct} className="w-32 h-3" />
          </div>
          <span className="text-xs text-slate-400">{scoredCount}/{allItems.length} KRAs scored</span>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[40%]">KRA Description</TableHead>
              <TableHead className="text-center w-20">Allotted</TableHead>
              <TableHead className="text-center w-24">Scored</TableHead>
              <TableHead className="text-center w-28">Progress</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {taskKras.length > 0 && (
              <TableRow><TableCell colSpan={5} className="bg-blue-50/60 py-2 text-xs font-semibold text-blue-800 uppercase tracking-wider">
                Task-Based KRAs ({taskKras.length})
              </TableCell></TableRow>
            )}
            {taskKras.map((item, idx) => {
              const origIdx = allItems.findIndex(a => a.kra_item_id === item.kra_item_id);
              return (
                <KraScoreRow key={item.kra_item_id} item={item} isEditing={editing}
                  onScoreChange={v => onScoreChange(origIdx, 'marks_scored', v)}
                  onRemarksChange={v => onRemarksChange(origIdx, 'remarks', v)} />
              );
            })}
            {nonTaskKras.length > 0 && (
              <TableRow><TableCell colSpan={5} className="bg-amber-50/60 py-2 text-xs font-semibold text-amber-800 uppercase tracking-wider">
                Non-Task-Based KRAs ({nonTaskKras.length})
              </TableCell></TableRow>
            )}
            {nonTaskKras.map((item, idx) => {
              const origIdx = allItems.findIndex(a => a.kra_item_id === item.kra_item_id);
              return (
                <KraScoreRow key={item.kra_item_id} item={item} isEditing={editing}
                  onScoreChange={v => onScoreChange(origIdx, 'marks_scored', v)}
                  onRemarksChange={v => onRemarksChange(origIdx, 'remarks', v)} />
              );
            })}
            <TableRow className="bg-slate-100 font-semibold">
              <TableCell className="text-sm">TOTAL</TableCell>
              <TableCell className="text-center text-sm">{totalAllotted}</TableCell>
              <TableCell className="text-center text-sm">{totalScored}</TableCell>
              <TableCell className="text-center"><Badge className={`text-xs ${pct >= 81 ? BAND_COLORS.Excellent : pct >= 61 ? BAND_COLORS.Good : pct >= 51 ? BAND_COLORS.Average : BAND_COLORS['Below Threshold']}`}>{pct}%</Badge></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Performance</h1>
          <p className="text-sm text-slate-500 mt-1">Monthly KRA evaluations &middot; Quarterly disbursement</p>
        </div>
        {admin && (
          <Button size="sm" variant="outline" onClick={sendReminders} disabled={sendingReminders} data-testid="send-reminders-btn" className="gap-1.5">
            <Bell className="w-3.5 h-3.5" /> {sendingReminders ? 'Sending...' : 'Send KRA Reminders'}
          </Button>
        )}
        {canManageTeam && !admin && (
          <Button size="sm" variant="outline" onClick={sendReminders} disabled={sendingReminders} data-testid="send-reminders-btn" className="gap-1.5">
            <Bell className="w-3.5 h-3.5" /> {sendingReminders ? 'Sending...' : 'Remind My Team'}
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100" data-testid="performance-tabs">
          <TabsTrigger value="my-evaluation" data-testid="tab-my-monthly">My Monthly</TabsTrigger>
          <TabsTrigger value="my-quarterly" data-testid="tab-my-quarterly">My Quarterly</TabsTrigger>
          {canManageTeam && <TabsTrigger value="monthly-all" data-testid="tab-all-monthly">{admin ? 'All' : 'Team'} — Monthly</TabsTrigger>}
          {canManageTeam && <TabsTrigger value="quarterly-all" data-testid="tab-all-quarterly">{admin ? 'All' : 'Team'} — Quarterly</TabsTrigger>}
          <TabsTrigger value="bands" data-testid="tab-bands">Score Bands</TabsTrigger>
          <TabsTrigger value="daily-checkin" data-testid="tab-daily-checkin">Daily Check-in</TabsTrigger>
        </TabsList>

        {/* ─── My Monthly Evaluation ─── */}
        <TabsContent value="my-evaluation">
          <div className="flex items-center gap-3 mb-4">
            <CalendarDays className="w-4 h-4 text-slate-500" />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48" data-testid="month-select"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent>{periods.months.map(m => <SelectItem key={m.name} value={m.name}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            {evaluation && <Badge className={`text-xs ${STATUS_COLORS[evaluation.status] || ''}`}>{evaluation.status?.replace('_', ' ')}</Badge>}
            {evaluation && evaluation.kra_scores.length > 0 && !isEditing && !empRemarksEditing && (
              <>
                <Button size="sm" variant="outline" onClick={() => startEditing(evaluation.kra_scores)} data-testid="edit-my-scores">
                  <Star className="w-3.5 h-3.5 mr-1" /> {admin ? 'Score KRAs' : 'Self Evaluate'}
                </Button>
                <Button size="sm" variant="outline" onClick={startEmpRemarksEdit} data-testid="edit-my-monthly-remarks">
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> My Monthly Remarks
                </Button>
              </>
            )}
            {isEditing && (
              <div className="flex gap-2">
                <Button size="sm" onClick={saveMyScores} className="bg-[#0D2850]" data-testid="save-my-scores">
                  <Save className="w-3.5 h-3.5 mr-1" /> Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              </div>
            )}
            {empRemarksEditing && (
              <div className="flex gap-2">
                <Button size="sm" onClick={saveEmpRemarks} className="bg-[#0D2850]" data-testid="save-my-monthly-remarks">
                  <Save className="w-3.5 h-3.5 mr-1" /> Save Remarks
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEmpRemarksEditing(false)}>Cancel</Button>
              </div>
            )}
          </div>
          {empRemarksEditing ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#0D2850]" />My Monthly Remarks — {monthLabel(selectedMonth)}</CardTitle>
                <p className="text-xs text-slate-400 mt-1">Add your own remarks per KRA before your manager scores them. Your manager will see these when evaluating.</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {empRemarksScores.map((s, idx) => (
                  <div key={s.kra_item_id} className="border rounded-lg p-3 border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`text-[10px] ${s.category === 'Task-Based' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>{s.category || '—'}</Badge>
                      <p className="text-sm font-medium text-slate-700">{s.description}</p>
                    </div>
                    <Textarea
                      value={s.employee_remarks || ''}
                      onChange={e => updateEmpRemarks(idx, e.target.value)}
                      rows={2}
                      placeholder="What went well, blockers, achievements..."
                      className="text-xs"
                      data-testid={`emp-remarks-${s.kra_item_id}`}
                    />
                  </div>
                ))}
                {empRemarksScores.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No KRAs to add remarks for.</p>
                )}
              </CardContent>
            </Card>
          ) : (evaluation && evaluation.kra_scores.length > 0 ? (
            <Card>
              <CardContent className="p-4">
                {renderKraTable(evaluation.kra_scores, isEditing, editScores, updateScore, updateScore)}
                {(evaluation.kra_scores.some(s => s.employee_remarks)) && (
                  <div className="mt-4 border-t pt-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Employee Self-Remarks</p>
                    <div className="space-y-1.5">
                      {evaluation.kra_scores.filter(s => s.employee_remarks).map(s => (
                        <div key={s.kra_item_id} className="text-xs bg-slate-50 rounded p-2">
                          <span className="text-slate-500">{s.description}: </span>
                          <span className="text-slate-800">{s.employee_remarks}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="p-8 text-center text-slate-500 text-sm">No KRAs assigned for this period. Contact your manager or HR.</CardContent></Card>
          ))}
        </TabsContent>

        {/* ─── My Quarterly Summary ─── */}
        <TabsContent value="my-quarterly">
          <div className="flex items-center gap-3 mb-4">
            <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
              <SelectTrigger className="w-64" data-testid="quarter-select"><SelectValue placeholder="Quarter" /></SelectTrigger>
              <SelectContent>{periods.quarters.map(q => <SelectItem key={q.name} value={q.name}>{q.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {quarterly && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card><CardContent className="p-4 text-center">
                  <TrendingUp className="w-5 h-5 mx-auto text-[#0D2850] mb-1" />
                  <div className="text-2xl font-semibold">{quarterly.average_score}%</div>
                  <div className="text-xs text-slate-500">Avg Score (3 months)</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <Award className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                  <Badge className={`text-xs ${BAND_COLORS[quarterly.band] || ''}`}>{quarterly.band}</Badge>
                  <div className="text-xs text-slate-500 mt-1">{quarterly.payout_percentage}% Payout</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <IndianRupee className="w-5 h-5 mx-auto text-slate-500 mb-1" />
                  <div className="text-2xl font-semibold">{(quarterly.kra_amount || 0).toLocaleString('en-IN')}</div>
                  <div className="text-xs text-slate-500">KRA Amount</div>
                </CardContent></Card>
                <Card><CardContent className="p-4 text-center">
                  <IndianRupee className="w-5 h-5 mx-auto text-emerald-600 mb-1" />
                  <div className="text-2xl font-semibold text-emerald-700">{(quarterly.quarterly_payout || 0).toLocaleString('en-IN')}</div>
                  <div className="text-xs text-slate-500">Quarterly Payout</div>
                </CardContent></Card>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Monthly Score Breakdown</CardTitle></CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Month</TableHead>
                      <TableHead className="text-center">Score / Allotted</TableHead>
                      <TableHead className="text-center">Percentage</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {quarterly.monthly_scores.map(ms => (
                        <TableRow key={ms.month}>
                          <TableCell className="font-medium text-sm">{ms.month_name}</TableCell>
                          <TableCell className="text-center text-sm">{ms.total_scored || '—'} / {ms.total_allotted || '—'}</TableCell>
                          <TableCell className="text-center"><span className="text-lg font-semibold">{ms.score || '—'}</span>%</TableCell>
                          <TableCell className="text-center"><Badge className={`text-xs ${STATUS_COLORS[ms.status] || ''}`}>{ms.status?.replace('_', ' ')}</Badge></TableCell>
                          <TableCell><Progress value={ms.score || 0} className="w-24 h-2" /></TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-slate-50 font-semibold">
                        <TableCell>Average</TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-center text-lg">{quarterly.average_score}%</TableCell>
                        <TableCell className="text-center"><Badge className={`text-xs ${BAND_COLORS[quarterly.band] || ''}`}>{quarterly.band}</Badge></TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ─── Admin/Manager: All Employees Monthly ─── */}
        {canManageTeam && (
          <TabsContent value="monthly-all">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <CalendarDays className="w-4 h-4 text-slate-500" />
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-48"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>{periods.months.map(m => <SelectItem key={m.name} value={m.name}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-xs text-slate-400">{allMonthly.length} {admin ? 'employees' : 'team members'} with KRA</span>
              <Button size="sm" variant="outline" onClick={() => { setUploadOpen(true); setUploadResult(null); setUploadFile(null); }} className="gap-1 ml-auto" data-testid="excel-upload-btn">
                <Upload className="w-3.5 h-3.5" /> Excel Upload
              </Button>
              <Button size="sm" variant="outline" onClick={loadEmpWithoutKra} className="gap-1" data-testid="add-kra-member-btn">
                <Users className="w-3.5 h-3.5" /> Add Member
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-center">KRAs</TableHead>
                    <TableHead className="text-center">Scored / Allotted</TableHead>
                    <TableHead className="text-center">%</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">KRA Amt</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {allMonthly.map(e => (
                      <React.Fragment key={e.employee_id}>
                        <TableRow className="hover:bg-[#F7FAFF] cursor-pointer" onClick={() => loadEmpEval(e.employee_id)} data-testid={`perf-row-${e.employee_id}`}>
                          <TableCell className="font-medium text-sm">{e.name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{e.department}</TableCell>
                          <TableCell className="text-center text-sm">{e.kra_count}</TableCell>
                          <TableCell className="text-center text-sm">{e.total_scored || 0} / {e.total_marks_allotted || '—'}</TableCell>
                          <TableCell className="text-center text-sm font-semibold">{e.score ? `${e.score}%` : '—'}</TableCell>
                          <TableCell className="text-center"><Badge className={`text-xs ${STATUS_COLORS[e.status] || ''}`}>{(e.status || 'pending').replace('_', ' ')}</Badge></TableCell>
                          <TableCell className="text-right text-sm">{e.kra_amount ? `₹${e.kra_amount.toLocaleString('en-IN')}` : '—'}</TableCell>
                          <TableCell className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(ev) => { ev.stopPropagation(); loadKraDefn(e.employee_id); }} title="View KRA Definitions">
                              <FileText className="w-3.5 h-3.5" />
                            </Button>
                            {expandedEmp === e.employee_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </TableCell>
                        </TableRow>
                        {expandedEmp === e.employee_id && empEval && (
                          <TableRow><TableCell colSpan={8} className="bg-slate-50/80 p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <h4 className="font-semibold text-sm text-slate-700">{e.name} — {monthLabel(selectedMonth)}</h4>
                              {!empEditing ? (
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={startEmpEditing} data-testid={`score-btn-${e.employee_id}`}>
                                    <Star className="w-3 h-3 mr-1" /> Score KRAs
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(ev) => { ev.stopPropagation(); setAddItemEmpId(e.employee_id); setAddItemOpen(true); }} data-testid={`add-kra-${e.employee_id}`}>
                                    <Plus className="w-3 h-3 mr-1" /> Add KRA
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={saveEmpScores} className="h-7 text-xs bg-[#0D2850]" data-testid={`save-scores-${e.employee_id}`}>
                                    <Save className="w-3 h-3 mr-1" /> Save
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEmpEditing(false)}>Cancel</Button>
                                </div>
                              )}
                            </div>
                            {empEval.kra_scores.length === 0 ? (
                              <p className="text-sm text-slate-500">No KRAs defined. Use "Add KRA" to create definitions.</p>
                            ) : (
                              renderKraTable(empEval.kra_scores, empEditing, empEditScores, updateEmpScore, updateEmpScore)
                            )}
                          </TableCell></TableRow>
                        )}
                      </React.Fragment>
                    ))}
                    {allMonthly.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500 text-sm">No employees with KRA definitions</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ─── Admin/Manager: All Employees Quarterly ─── */}
        {canManageTeam && (
          <TabsContent value="quarterly-all">
            <div className="flex items-center gap-3 mb-4">
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger className="w-64"><SelectValue placeholder="Quarter" /></SelectTrigger>
                <SelectContent>{periods.quarters.map(q => <SelectItem key={q.name} value={q.name}>{q.label}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-xs text-slate-400">{allQuarterly.length} employees</span>
            </div>
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    {periods.quarters.find(q => q.name === selectedQuarter)?.months?.map(m => {
                      const mNum = parseInt(m.split('-')[1]);
                      const names = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      return <TableHead key={m} className="text-center text-xs">{names[mNum]}</TableHead>;
                    })}
                    <TableHead className="text-center">Avg</TableHead>
                    <TableHead className="text-center">Band</TableHead>
                    <TableHead className="text-right">KRA Amt</TableHead>
                    <TableHead className="text-right">Payout</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {allQuarterly.map(e => (
                      <TableRow key={e.employee_id} className="hover:bg-[#F7FAFF]" data-testid={`qtr-row-${e.employee_id}`}>
                        <TableCell className="font-medium text-sm">{e.name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{e.department}</TableCell>
                        {(e.monthly || []).map(m => (
                          <TableCell key={m.month} className="text-center text-sm">{m.score ? `${m.score}%` : '—'}</TableCell>
                        ))}
                        <TableCell className="text-center font-semibold text-sm">{e.score ? `${e.score}%` : '—'}</TableCell>
                        <TableCell className="text-center"><Badge className={`text-xs ${BAND_COLORS[e.band] || ''}`}>{e.band}</Badge></TableCell>
                        <TableCell className="text-right text-sm">{e.kra_amount ? `₹${e.kra_amount.toLocaleString('en-IN')}` : '—'}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">{e.kra_payout ? `₹${e.kra_payout.toLocaleString('en-IN')}` : '—'}</TableCell>
                      </TableRow>
                    ))}
                    {allQuarterly.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500 text-sm">No data</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ─── Daily Check-in ─── */}
        <TabsContent value="daily-checkin">
          <div className="space-y-6">
            {/* My Check-in */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><CheckSquare className="w-4 h-4 text-[#0D2850]" />My Daily KRA Check-in</CardTitle></CardHeader>
              <CardContent>
                {!myKraDefs || (myKraDefs.task_based_kras?.length === 0 && myKraDefs.non_task_kras?.length === 0) ? (
                  <p className="text-sm text-slate-500 py-4">No KRAs assigned. Contact your manager.</p>
                ) : (
                  <>
                    <p className="text-xs text-slate-400 mb-3">Check the KRAs you worked on today and add optional remarks. ({(myCheckin?.checked_kras || []).length} checked)</p>
                    {myKraDefs.task_based_kras?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-2">Task-Based</p>
                        <div className="space-y-1.5">
                          {myKraDefs.task_based_kras.map(k => {
                            const checked = isKraChecked(k.id);
                            return (
                              <div key={k.id} className={`rounded-lg border transition-colors ${checked ? 'bg-emerald-50 border-emerald-200' : 'border-slate-200'}`} data-testid={`checkin-kra-${k.id}`}>
                                <label className="flex items-center gap-3 p-2.5 cursor-pointer">
                                  <input type="checkbox" checked={checked} onChange={() => toggleCheckin(k.id)} className="w-4 h-4 rounded accent-emerald-600" />
                                  <span className={`text-sm ${checked ? 'text-emerald-800 font-medium' : 'text-slate-700'}`}>{k.description}</span>
                                  <span className="ml-auto text-xs text-slate-400">{k.frequency}</span>
                                </label>
                                {checked && (
                                  <div className="px-2.5 pb-2.5">
                                    <Input
                                      value={getCheckinRemarks(k.id)}
                                      onChange={e => updateCheckinRemarks(k.id, e.target.value)}
                                      onBlur={persistCheckinRemarks}
                                      placeholder="Today's remarks (optional)..."
                                      className="h-8 text-xs bg-white"
                                      data-testid={`checkin-remarks-${k.id}`}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {myKraDefs.non_task_kras?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-2">Non-Task-Based</p>
                        <div className="space-y-1.5">
                          {myKraDefs.non_task_kras.map(k => {
                            const checked = isKraChecked(k.id);
                            return (
                              <div key={k.id} className={`rounded-lg border transition-colors ${checked ? 'bg-emerald-50 border-emerald-200' : 'border-slate-200'}`} data-testid={`checkin-nontask-${k.id}`}>
                                <label className="flex items-center gap-3 p-2.5 cursor-pointer">
                                  <input type="checkbox" checked={checked} onChange={() => toggleCheckin(k.id)} className="w-4 h-4 rounded accent-emerald-600" />
                                  <span className={`text-sm ${checked ? 'text-emerald-800 font-medium' : 'text-slate-700'}`}>{k.description}</span>
                                </label>
                                {checked && (
                                  <div className="px-2.5 pb-2.5">
                                    <Input
                                      value={getCheckinRemarks(k.id)}
                                      onChange={e => updateCheckinRemarks(k.id, e.target.value)}
                                      onBlur={persistCheckinRemarks}
                                      placeholder="Today's remarks (optional)..."
                                      className="h-8 text-xs bg-white"
                                      data-testid={`checkin-nontask-remarks-${k.id}`}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Team Check-ins (Admin/Manager only) */}
            {canManageTeam && teamCheckins && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-[#0D2850]" />{admin ? 'Team' : 'My Team'} Daily Activity — {teamCheckins.date}</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead className="text-center">KRAs Checked</TableHead>
                      <TableHead className="text-center">Total KRAs</TableHead>
                      <TableHead className="text-center">Progress</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(teamCheckins.checkins || []).map(c => {
                        const pct = c.total_kras > 0 ? Math.round((c.checked_count / c.total_kras) * 100) : 0;
                        return (
                          <TableRow key={c.employee_id} data-testid={`team-checkin-${c.employee_id}`}>
                            <TableCell className="font-medium text-sm">{c.name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{c.department}</TableCell>
                            <TableCell className="text-center text-sm font-semibold">{c.checked_count}</TableCell>
                            <TableCell className="text-center text-sm text-slate-500">{c.total_kras}</TableCell>
                            <TableCell className="text-center"><div className="flex items-center gap-2 justify-center"><Progress value={pct} className="w-16 h-2" /><span className="text-xs text-slate-500">{pct}%</span></div></TableCell>
                            <TableCell className="text-center">
                              <Badge className={`text-[10px] ${c.has_checkin ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                {c.has_checkin ? 'Checked In' : 'Not Yet'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(teamCheckins.checkins || []).length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center py-6 text-sm text-slate-400">No check-ins today</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ─── Score Bands ─── */}
        <TabsContent value="bands">
          <Card>
            <CardHeader><CardTitle className="text-sm font-semibold">KRA Score Bands & Payout Structure</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>Band</TableHead><TableHead>Score Range</TableHead><TableHead>KRA Payout %</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bands.map((b, i) => (
                    <TableRow key={i}>
                      <TableCell><Badge className={`text-xs ${BAND_COLORS[b.label] || ''}`}>{b.label}</Badge></TableCell>
                      <TableCell className="text-sm">{b.min}% – {b.max}%</TableCell>
                      <TableCell className="text-sm font-semibold">{b.payout_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs text-slate-600 space-y-1">
                <p><strong>Scoring:</strong> Each KRA has marks allotted. Manager scores each KRA individually. Final % = (Total Scored / Total Allotted) x 100.</p>
                <p><strong>Evaluation Cycle:</strong> KRAs are evaluated every month.</p>
                <p><strong>Disbursement:</strong> Quarterly — average of 3 monthly % scores determines the payout band.</p>
                <p><strong>Formula:</strong> Quarterly Payout = KRA Amount x Payout %</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add KRA Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Add KRA Item</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Description</Label><Textarea value={addItemForm.description} onChange={e => setAddItemForm({...addItemForm, description: e.target.value})} rows={2} data-testid="add-kra-desc" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Category</Label>
                <Select value={addItemForm.category} onValueChange={v => setAddItemForm({...addItemForm, category: v})}>
                  <SelectTrigger data-testid="add-kra-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task">Task-Based</SelectItem>
                    <SelectItem value="non_task">Non-Task</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Frequency</Label><Input value={addItemForm.frequency} onChange={e => setAddItemForm({...addItemForm, frequency: e.target.value})} placeholder="Daily, Weekly..." data-testid="add-kra-frequency" /></div>
              <div><Label>Marks Allotted</Label><Input type="number" min="0" step="0.5" value={addItemForm.marks_allotted} onChange={e => setAddItemForm({...addItemForm, marks_allotted: e.target.value})} data-testid="add-kra-marks" /></div>
            </div>
            <Button onClick={handleAddItem} className="w-full bg-[#0D2850]" data-testid="add-kra-submit">Add KRA</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View KRA Definitions Dialog */}
      <Dialog open={viewDefnOpen} onOpenChange={setViewDefnOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>KRA Definitions</DialogTitle>
            {canManageTeam && viewDefnEmpId && (
              <Button size="sm" variant="outline" className="w-fit mt-2 gap-1" onClick={() => { setAddItemEmpId(viewDefnEmpId); setAddItemOpen(true); }} data-testid="add-kra-from-defn">
                <Plus className="w-3.5 h-3.5" /> Add KRA
              </Button>
            )}
          </DialogHeader>
          {kraDefn && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-slate-600">Total Marks Allotted: <strong>{kraDefn.total_marks_allotted}</strong></div>
              {kraDefn.task_based_kras?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-2">Task-Based KRAs ({kraDefn.task_based_kras.length})</h4>
                  <Table>
                    <TableHeader><TableRow className="bg-blue-50/50">
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead className="text-center">Marks</TableHead>
                      {canManageTeam && <TableHead className="w-8"></TableHead>}
                    </TableRow></TableHeader>
                    <TableBody>
                      {kraDefn.task_based_kras.map((k, i) => (
                        <TableRow key={k.id}>
                          <TableCell className="text-xs text-slate-400">{k.sl_no || i+1}</TableCell>
                          <TableCell className="text-sm">{k.description}</TableCell>
                          <TableCell className="text-xs text-slate-500">{k.frequency}</TableCell>
                          <TableCell className="text-center text-sm font-semibold">{k.marks_allotted}</TableCell>
                          {canManageTeam && (
                            <TableCell>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteKraItem(k.id)} data-testid={`delete-kra-${k.id}`} title="Remove KRA">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {kraDefn.non_task_kras?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-2">Non-Task-Based KRAs ({kraDefn.non_task_kras.length})</h4>
                  <Table>
                    <TableHeader><TableRow className="bg-amber-50/50">
                      <TableHead>Description</TableHead>
                      <TableHead className="text-center">Marks</TableHead>
                      {canManageTeam && <TableHead className="w-8"></TableHead>}
                    </TableRow></TableHeader>
                    <TableBody>
                      {kraDefn.non_task_kras.map(k => (
                        <TableRow key={k.id}>
                          <TableCell className="text-sm">{k.description}</TableCell>
                          <TableCell className="text-center text-sm font-semibold">{k.marks_allotted}</TableCell>
                          {canManageTeam && (
                            <TableCell>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteKraItem(k.id)} data-testid={`delete-kra-${k.id}`} title="Remove KRA">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {(!kraDefn.task_based_kras || kraDefn.task_based_kras.length === 0) && (!kraDefn.non_task_kras || kraDefn.non_task_kras.length === 0) && (
                <p className="text-sm text-slate-500 text-center py-6">No KRAs defined yet. Click "Add KRA" to create one.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Excel Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg bg-white border-slate-200 shadow-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Upload KRAs via Excel</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Upload a .xlsx file with columns: <code className="text-[10px] bg-slate-100 px-1 rounded">employee_code, category, description, target, frequency, marks_allotted</code></p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Button variant="outline" size="sm" className="w-full gap-1" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-3.5 h-3.5" /> Download Sample Template
            </Button>
            <div>
              <Label className="text-xs">Excel File</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="text-xs" data-testid="upload-file-input" />
            </div>
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={uploadMode} onValueChange={setUploadMode}>
                <SelectTrigger data-testid="upload-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="replace">Replace — wipes existing KRAs for each employee</SelectItem>
                  <SelectItem value="append">Append — adds to existing KRAs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={submitExcelUpload} disabled={uploading || !uploadFile} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="upload-submit-btn">
              {uploading ? 'Uploading…' : 'Upload & Apply'}
            </Button>
            {uploadResult && (
              <div className="text-xs space-y-2 border-t pt-3">
                <p className="font-medium text-slate-700">{uploadResult.message || 'Upload result'}</p>
                {uploadResult.applied?.length > 0 && (
                  <div>
                    <p className="font-semibold text-emerald-700 mb-1">✓ Applied ({uploadResult.applied.length})</p>
                    <ul className="space-y-0.5 pl-3">
                      {uploadResult.applied.map((a, i) => (
                        <li key={i} className="text-slate-600">• {a.employee_name} ({a.employee_code}) — {a.task_kra_count} task + {a.non_task_kra_count} non-task = {a.total_marks} marks</li>
                      ))}
                    </ul>
                  </div>
                )}
                {uploadResult.skipped?.length > 0 && (
                  <div>
                    <p className="font-semibold text-amber-700 mb-1">⚠ Skipped ({uploadResult.skipped.length})</p>
                    <ul className="space-y-0.5 pl-3">
                      {uploadResult.skipped.map((s, i) => (
                        <li key={i} className="text-slate-600">• {s.employee_code} — {s.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {uploadResult.errors?.length > 0 && (
                  <div>
                    <p className="font-semibold text-red-700 mb-1">✗ Errors ({uploadResult.errors.length})</p>
                    <ul className="space-y-0.5 pl-3">
                      {uploadResult.errors.slice(0, 10).map((err, i) => (
                        <li key={i} className="text-red-600">• {err}</li>
                      ))}
                      {uploadResult.errors.length > 10 && <li className="text-slate-400">• …and {uploadResult.errors.length - 10} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add KRA Member</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Select an employee to add to KRA. Default non-task KRAs will be created automatically. You'll then be prompted to add task-based performance parameters.</p>
          </DialogHeader>
          <div className="space-y-4">
            {empWithoutKra.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">All employees already have KRA definitions.</p>
            ) : (
              <>
                <Select value={selectedNewMember} onValueChange={setSelectedNewMember}>
                  <SelectTrigger data-testid="select-new-kra-member"><SelectValue placeholder="Select employee..." /></SelectTrigger>
                  <SelectContent>
                    {empWithoutKra.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code}) — {e.department}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="w-full bg-[#0D2850] hover:bg-[#0B2246]" onClick={handleAddMember} disabled={addingMember || !selectedNewMember} data-testid="confirm-add-member">
                  {addingMember ? 'Adding...' : 'Add & Configure KRAs'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
